import React from 'react';
import { DeckSlideWrapper } from '../DeckSlideWrapper';
import { Check, X, Minus } from 'lucide-react';

export const Slide07CompetitiveMatrix: React.FC = () => {
  const competitors = [
    {
      feature: 'In-Memory Bi-directional DLP Vault',
      workouse: true,
      portkey: false,
      cf: false,
      litellm: 'partial',
      legacyDlp: 'partial',
    },
    {
      feature: 'Isolated V8 Sandboxes & HITL Dry-Run',
      workouse: true,
      portkey: false,
      cf: 'partial',
      litellm: false,
      legacyDlp: false,
    },
    {
      feature: 'HashiCorp Vault & Zero Credential Ingress',
      workouse: true,
      portkey: false,
      cf: false,
      litellm: false,
      legacyDlp: 'partial',
    },
    {
      feature: 'WAP Standardized Connector Plugins',
      workouse: true,
      portkey: false,
      cf: false,
      litellm: false,
      legacyDlp: false,
    },
    {
      feature: 'Atomic USD Budget Hard/Soft Caps',
      workouse: true,
      portkey: 'partial',
      cf: false,
      litellm: 'partial',
      legacyDlp: false,
    },
    {
      feature: 'Air-Gapped Self-Hosted Distribution',
      workouse: true,
      portkey: false,
      cf: false,
      litellm: true,
      legacyDlp: true,
    },
    {
      feature: 'Haskell Parsec Async Parsing Worker',
      workouse: true,
      portkey: false,
      cf: false,
      litellm: false,
      legacyDlp: false,
    },
  ];

  return (
    <DeckSlideWrapper
      slideNumber={7}
      totalSlides={12}
      category="Competitive Advantage"
      title="Why Workouse Wins: Complete Zero-Trust Moat"
      subtitle="Other AI gateways are just routing proxies. Workouse is a complete Data Sovereignty & Financial Governor."
    >
      <div className="relative">
        <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-navy-700 bg-white/90 dark:bg-navy-850/80 shadow-sm backdrop-blur-sm">
          <table className="w-full text-left text-[11px] sm:text-xs font-sans min-w-[620px]">
            <thead>
              <tr className="border-b border-slate-200 dark:border-navy-700 bg-slate-100/90 dark:bg-navy-900/90 text-slate-700 dark:text-slate-300 font-mono">
                <th className="py-2.5 sm:py-3 px-3 sm:px-4 font-bold text-slate-900 dark:text-white">Capability</th>
                <th className="py-2.5 sm:py-3 px-3 sm:px-4 font-bold text-gold-800 dark:text-gold-400 bg-gold-500/15 dark:bg-gold-500/20 border-x border-gold-500/30">
                  Workouse AI
                </th>
                <th className="py-2.5 sm:py-3 px-3 sm:px-4 font-semibold text-slate-500">Portkey</th>
                <th className="py-2.5 sm:py-3 px-3 sm:px-4 font-semibold text-slate-500">Cloudflare AI</th>
                <th className="py-2.5 sm:py-3 px-3 sm:px-4 font-semibold text-slate-500">LiteLLM</th>
                <th className="py-2.5 sm:py-3 px-3 sm:px-4 font-semibold text-slate-500">Netskope (Legacy)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-navy-700/60">
              {competitors.map((row, idx) => (
                <tr
                  key={idx}
                  className="hover:bg-slate-50 dark:hover:bg-navy-800/40 transition-colors font-medium"
                >
                  <td className="py-2 sm:py-2.5 px-3 sm:px-4 text-slate-900 dark:text-slate-200 font-medium">
                    {row.feature}
                  </td>

                  {/* Workouse */}
                  <td className="py-2 sm:py-2.5 px-3 sm:px-4 bg-gold-500/10 dark:bg-gold-500/10 border-x border-gold-500/20 text-center">
                    <span className="inline-flex items-center gap-1 text-gold-800 dark:text-gold-400 font-bold font-mono text-[11px] sm:text-xs">
                      <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4 stroke-[3]" />
                      <span>Native</span>
                    </span>
                  </td>

                  {/* Portkey */}
                  <td className="py-2 sm:py-2.5 px-3 sm:px-4 text-center">
                    {row.portkey === true ? (
                      <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-emerald-500 mx-auto" />
                    ) : row.portkey === 'partial' ? (
                      <Minus className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-amber-500 mx-auto" />
                    ) : (
                      <X className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-slate-300 dark:text-slate-600 mx-auto" />
                    )}
                  </td>

                  {/* Cloudflare */}
                  <td className="py-2 sm:py-2.5 px-3 sm:px-4 text-center">
                    {row.cf === true ? (
                      <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-emerald-500 mx-auto" />
                    ) : row.cf === 'partial' ? (
                      <Minus className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-amber-500 mx-auto" />
                    ) : (
                      <X className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-slate-300 dark:text-slate-600 mx-auto" />
                    )}
                  </td>

                  {/* LiteLLM */}
                  <td className="py-2 sm:py-2.5 px-3 sm:px-4 text-center">
                    {row.litellm === true ? (
                      <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-emerald-500 mx-auto" />
                    ) : row.litellm === 'partial' ? (
                      <Minus className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-amber-500 mx-auto" />
                    ) : (
                      <X className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-slate-300 dark:text-slate-600 mx-auto" />
                    )}
                  </td>

                  {/* Legacy DLP */}
                  <td className="py-2 sm:py-2.5 px-3 sm:px-4 text-center">
                    {row.legacyDlp === true ? (
                      <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-emerald-500 mx-auto" />
                    ) : row.legacyDlp === 'partial' ? (
                      <Minus className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-amber-500 mx-auto" />
                    ) : (
                      <X className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-slate-300 dark:text-slate-600 mx-auto" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Key Takeaway */}
      <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 rounded-xl bg-slate-900 dark:bg-navy-950 text-slate-300 border border-slate-700 dark:border-navy-700 text-[11px] sm:text-xs font-mono flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1.5">
        <span>
          <strong className="text-gold-400">Our Unfair Advantage:</strong> Bi-directional ephemeral RAM tokenization + atomic financial caps + strict functional reliability.
        </span>
        <span className="hidden md:inline text-[11px] text-slate-400">Zero vendor lock-in</span>
      </div>
    </DeckSlideWrapper>
  );
};
